import { isColdTub } from '../../../utils/brand';

/**
 * V1 / Coldfire active faults, decoded from the `telemetry/errors` shape (the live
 * topic, and the details snapshot's `channels.errors`).
 *
 *   - Yoctub (version 1): { code } — a 64-bit bitmask of fault bits, as text or number.
 *   - Coldfire (version 0): one boolean per fault (`spaboyComm` for the SpaBoy comms fault).
 *
 * Named as the old Android/iOS apps named them (ER codes, their titles, priorities and
 * videos). Dealers see every fault, not only the ones those apps showed: highest
 * priority first, then the rest by number, unnamed bits last as the bare code.
 */
export interface V1Error {
  /** 'ER08' */
  code: string;
  label: string;
  /** The old apps' help video, if they had one. */
  link: string | null;
}

const SPA = isColdTub ? 'Tub' : 'Spa';
const VIDEO = 'https://youtu.be/L5Rokwlfsko?t=';

interface Known {
  label: string;
  /** The old apps' display priority; 0 = they did not show it. */
  priority: number;
  video?: string;
}

/**
 * ER number (= Yoctub bit). Bits 16-19 and 29-63 have no name; they are still shown,
 * as the bare code, after the named ones.
 */
export const V1_ERRORS: Record<number, Known> = {
  0: { label: 'No Flow Error', priority: 0 },
  1: { label: 'Flow Switch Error', priority: 0 },
  2: { label: 'Heater too Hot', priority: 32, video: `${VIDEO}226` },
  3: { label: 'Water too Hot', priority: 30, video: `${VIDEO}310` },
  4: { label: 'Water Temp Probe Disconnected', priority: 28, video: `${VIDEO}335` },
  5: { label: `${SPA} High Limit`, priority: 0, video: `${VIDEO}368` },
  6: { label: 'Eeprom', priority: 0 },
  7: { label: 'Freeze Protect', priority: 26, video: `${VIDEO}454` },
  8: { label: 'High pH Error', priority: 24, video: `${VIDEO}477` },
  9: { label: 'Heater Probe Disconnected', priority: 22, video: `${VIDEO}500` },
  10: { label: 'Heater Probe Test Failed', priority: 0 },
  11: { label: 'SpaBoy Comms Error', priority: 20, video: `${VIDEO}558` },
  12: { label: `Heater ${SPA} Temperature`, priority: 0 },
  13: { label: `Heater Way Above ${SPA}`, priority: 18, video: `${VIDEO}648` },
  14: { label: 'Orp Not Responding to Production', priority: 16, video: `${VIDEO}699` },
  15: { label: 'PH low (less than 6.5)', priority: 14, video: `${VIDEO}779` },
  20: { label: 'P1L No Current', priority: 0 },
  21: { label: 'P1H No Current', priority: 0 },
  22: { label: 'P2 No Current', priority: 0 },
  23: { label: 'P3 No Current', priority: 0 },
  24: { label: 'SDS No Current', priority: 0 },
  25: { label: 'YESS No Current', priority: 0 },
  26: { label: 'On No Current', priority: 0 },
  27: { label: 'H1 No Current', priority: 0 },
  28: { label: 'H2 No Current', priority: 0 },
};

/** Coldfire flag → ER number, as the old apps mapped them. */
const COLDFIRE_FLAGS: Record<string, number> = {
  noFlow: 0,
  flowSwitch: 1,
  heaterOverTemperature: 2,
  spaOverTemperature: 3,
  spaTemperatureProbe: 4,
  spaHighLimit: 5,
  eeprom: 6,
  freezeProtect: 7,
  phHigh: 8,
  hd: 9,
  hpt: 10,
  spaboyComm: 11,
};

const toError = (er: number): V1Error => ({
  code: `ER${String(er).padStart(2, '0')}`,
  label: V1_ERRORS[er]?.label ?? '',
  link: V1_ERRORS[er]?.video ?? null,
});

// Highest priority first, then named before unnamed, then by number.
const rank = (er: number) => [V1_ERRORS[er]?.priority ?? 0, V1_ERRORS[er] ? 1 : 0];

const byPriority = (ers: number[]): V1Error[] =>
  Array.from(new Set(ers))
    .sort((a, b) => {
      const [pa, na] = rank(a);
      const [pb, nb] = rank(b);
      return pb - pa || nb - na || a - b;
    })
    .map(toError);

const BITS = Array.from({ length: 64 }, (_, bit) => bit);

const toBigInt = (code: unknown): bigint | null => {
  try {
    return BigInt(code as string | number);
  } catch {
    return null;
  }
};

export function yoctubErrors(code: unknown): V1Error[] {
  const mask = toBigInt(code);
  if (mask === null || mask === BigInt(0)) return [];
  const one = BigInt(1);
  return byPriority(
    BITS.filter((bit) => ((mask >> BigInt(bit)) & one) === one),
  );
}

export function coldfireErrors(payload: Record<string, unknown>): V1Error[] {
  return byPriority(
    Object.entries(COLDFIRE_FLAGS)
      .filter(([flag]) => payload[flag] === true || Number(payload[flag]) === 1)
      .map(([, er]) => er),
  );
}

/**
 * Whether the mobile app shows this fault: the ones the old Android/iOS apps showed
 * (priority above 0). The control page uses the app's rules; the details page shows all.
 */
export const isAppError = (error: V1Error): boolean => {
  const er = Number(error.code.slice(2));
  return (V1_ERRORS[er]?.priority ?? 0) > 0;
};

/** `telemetry/errors` → active faults; null for a payload that is not an object. */
export function v1ErrorsFromPayload(payload: unknown): V1Error[] | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  if (p.code !== undefined && p.code !== null) return yoctubErrors(p.code);
  return coldfireErrors(p);
}

/**
 * A Yoctub's `code` is a 64-bit bitmask; JSON.parse would round it to a double and
 * drop its low bits. Keep any such integer as a string before parsing.
 */
export const keepLongCodeExact = (json: string): string =>
  json.replace(/("code"\s*:\s*)(\d{16,})(?=\s*[,}])/g, '$1"$2"');
